<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink } from 'vue-router'
import type { DashboardCampaignFunnel } from '@/types/models'

const props = defineProps<{
  funnel: DashboardCampaignFunnel
}>()

const statusLabels: Record<string, string> = {
  draft: 'Draft',
  ready: 'Ready',
  live: 'Live',
  paused: 'Paused',
  completed: 'Completed',
}

const statusBadgeVariants: Record<string, 'neutral' | 'success' | 'warning'> = {
  live: 'success',
  paused: 'neutral',
  completed: 'neutral',
  draft: 'neutral',
  ready: 'warning',
}

// Funnel stages read left-to-right; "replied" also counts contacts that
// have since moved further (branched/handed off/etc.), same "ever
// replied" signal the API used, not a re-derivation of it.
const funnelStages = computed(() => {
  const stateCounts = props.funnel.state_counts
  return [
    { label: 'Queued', count: stateCounts.queued },
    { label: 'Messaged', count: stateCounts.messaged + stateCounts.replied + stateCounts.branched + stateCounts.handed_off + stateCounts.closed_negative + stateCounts.exhausted + stateCounts.opted_out },
    { label: 'Replied', count: stateCounts.replied + stateCounts.branched + stateCounts.handed_off },
    { label: 'Branched / handed off', count: stateCounts.branched + stateCounts.handed_off },
  ]
})

const replyRatePct = computed(() => `${Math.round(props.funnel.reply_rate * 1000) / 10}%`)

// Grayscale ramp for the branch-hit distribution bar — darkest first, so
// the highest-volume branches read as the most "solid" ink.
const branchRampClasses = ['bg-on-surface', 'bg-on-surface-variant', 'bg-outline', 'bg-outline-variant']

const ALPHABET_START_CHAR_CODE = 'A'.charCodeAt(0)
const ALPHABET_SIZE = 26

const branchTotal = computed(() =>
  props.funnel.branch_hit_distribution.reduce((sum, branch) => sum + branch.count, 0),
)

function branchPct(count: number) {
  if (branchTotal.value === 0) return 0
  return Math.round((count / branchTotal.value) * 1000) / 10
}

// Spreadsheet-style column labels (A, B, ... Z, AA, AB, ...) so labels stay
// unique no matter how many branches a funnel has.
function branchLetter(index: number) {
  let remaining = index
  let label = ''
  do {
    label = String.fromCharCode(ALPHABET_START_CHAR_CODE + (remaining % ALPHABET_SIZE)) + label
    remaining = Math.floor(remaining / ALPHABET_SIZE) - 1
  } while (remaining >= 0)
  return label
}
</script>

<template>
  <Card padding="lg">
    <div class="mb-6 flex items-start justify-between gap-3">
      <RouterLink
        :to="{ name: 'campaign-builder', params: { campaignId: props.funnel.campaign_id } }"
        class="cursor-pointer font-serif text-xl font-medium text-on-surface hover:text-on-surface-variant"
      >
        {{ props.funnel.name }}
      </RouterLink>
      <Badge
        :variant="statusBadgeVariants[props.funnel.status] ?? 'neutral'"
        class="shrink-0"
      >
        {{ statusLabels[props.funnel.status] ?? props.funnel.status }}
      </Badge>
    </div>
    <p class="-mt-4 mb-6 text-metadata text-on-surface-variant">
      {{ props.funnel.linkedin_account.display_name ?? 'Unknown account' }}
    </p>

    <dl class="mb-6 grid grid-cols-4 gap-2 text-center">
      <div
        v-for="stage in funnelStages"
        :key="stage.label"
      >
        <dt class="tabular-nums text-ui-label font-medium text-on-surface">
          {{ stage.count }}
        </dt>
        <dd class="mt-1 text-metadata text-on-surface-variant">
          {{ stage.label }}
        </dd>
      </div>
    </dl>

    <div class="flex items-center justify-between gap-4 border-t border-outline-variant/30 pt-4">
      <span class="text-metadata text-on-surface-variant">
        Reply rate: <span class="font-medium text-on-surface">{{ replyRatePct }}</span>
      </span>

      <div
        v-if="props.funnel.branch_hit_distribution.length"
        class="flex w-32 flex-col gap-1"
      >
        <div class="flex h-1.5 w-full overflow-hidden rounded-full">
          <div
            v-for="(branch, index) in props.funnel.branch_hit_distribution"
            :key="branch.branch_sequence_id ?? 'inbox'"
            :class="branchRampClasses[index % branchRampClasses.length]"
            :style="{ width: `${branchPct(branch.count)}%` }"
            :title="`${branch.name}: ${branch.count}`"
          />
        </div>
        <div class="flex justify-between text-metadata text-on-surface-variant">
          <span
            v-for="(branch, index) in props.funnel.branch_hit_distribution"
            :key="branch.branch_sequence_id ?? 'inbox'"
          >
            {{ branchLetter(index) }}
          </span>
        </div>
      </div>
    </div>
  </Card>
</template>
